import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { ZodType } from 'zod';
import { api } from '#client/lib/api';
import { useFormSubmit } from './useFormSubmit';

interface UseResourceFormOptions<TInput, TRecord> {
  /** API path and route base, e.g. `/panels`. */
  path: string;
  /** Present when editing. Its presence is what chooses PATCH over POST. */
  id?: string | undefined;
  /** The same schema the server validates with, from shared/schemas. */
  schema: ZodType<TInput>;
  /** Capitalised singular noun: "Panel" gives "Panel created" / "Panel updated". */
  noun: string;
  createMessage?: string | undefined;
  updateMessage?: string | undefined;
  /** Where to go on success. Defaults to the list at `path`. */
  redirectTo?: string | undefined;
  /**
   * Where to go after a *create*, worked out from the record the server
   * returned. Takes precedence over `redirectTo` on create only.
   *
   * Registration is the caller: landing on the new patient is what makes the
   * slip printable without searching for them again.
   */
  redirectOnCreate?: ((created: TRecord) => string) | undefined;
  /**
   * Last chance to shape the request body.
   *
   * Patient registration is the caller that needs it: the totals it sends are
   * derived from the selected tests, not typed, so they are computed at submit
   * rather than held in form state.
   */
  toBody?: ((values: TInput) => unknown) | undefined;
}

/**
 * Create-or-update against a resource, wired to the shared submit handling.
 *
 * Deriving the method from `id` in one place stops an edit form from silently
 * POSTing a duplicate.
 */
export function useResourceForm<TInput, TRecord = unknown>({
  path,
  id,
  schema,
  noun,
  createMessage,
  updateMessage,
  redirectTo,
  redirectOnCreate,
  toBody,
}: UseResourceFormOptions<TInput, TRecord>) {
  const navigate = useNavigate();
  const isEdit = id !== undefined;

  const submit = useCallback(
    (values: TInput) => {
      const body = toBody ? toBody(values) : values;
      return isEdit ? api.patch<TRecord>(`${path}/${id}`, body) : api.post<TRecord>(path, body);
    },
    [isEdit, path, id, toBody],
  );

  const onSuccess = useCallback(
    (created: TRecord) =>
      void navigate(!isEdit && redirectOnCreate ? redirectOnCreate(created) : (redirectTo ?? path)),
    [navigate, isEdit, redirectOnCreate, redirectTo, path],
  );

  const form = useFormSubmit({
    schema,
    submit,
    onSuccess,
    successMessage: isEdit
      ? (updateMessage ?? `${noun} updated`)
      : (createMessage ?? `${noun} created`),
  });

  return { ...form, isEdit };
}
